import type { CSSProperties, FC, ReactNode } from 'react'
import type { MotionValue } from 'framer-motion'
import type { DoodleName, DoodleTone } from '@/components/home/market-hero/hero-doodles'

import { useEffect, useRef, useState } from 'react'
import { motion, useMotionValue, useReducedMotion, useSpring, useTransform } from 'framer-motion'

import { cn } from '@/lib/utils'
import { FloatingListingCard } from '@/components/home/market-hero/floating-listing-card'
import { HeroDoodle } from '@/components/home/market-hero/hero-doodles'
import { MarketPhone } from '@/components/home/market-hero/market-phone'
import { TrustedBadge } from '@/components/home/market-hero/trusted-badge'

import { HERO_LISTING_POOLS } from '@/components/home/market-hero/hero-market-data'

/**
 * The marketplace composition beside the hero headline.
 *
 * Every object is real markup — the phone, each listing card, the sticker and
 * every hand-drawn mark — so the whole thing is one network request of nothing
 * at all. It replaced a 1.7MB transparent artwork, which is why a JS animation
 * library is affordable here rather than a regression.
 *
 * PLACEMENT. `place` is where a piece lands on desktop and `mobile` is where
 * it lands on a phone, both as percentages of this canvas, with `fs` the one
 * number that scales everything inside it (the pieces size themselves in `em`,
 * the canvas is a container, and `fs` is read in `cqw`). A `mobile` of `null`
 * means the piece is not rendered on a phone at all, which is what keeps the
 * small layout a DIFFERENT composition rather than a shrunken copy of the
 * wide one.
 *
 * MOTION. Four transforms have to coexist and an element can only hold one, so
 * each piece nests them: the piece itself carries placement and its resting
 * tilt (CSS, and therefore per breakpoint), then parallax, then the entrance,
 * then the idle drift, then the card's own hover. Rotation is animated as a
 * DELTA against the CSS tilt, so the pieces settle at their breakpoint's
 * rotation without JS ever needing to know which breakpoint is live.
 */

const SUMMARY = 'A preview of the Bazio marketplace: a phone showing a featured listing from a verified seller, surrounded by floating cards for usernames, crypto deals, channels, social accounts and local deals, with a Trusted sticker.'

/** How far the pointer moves the composition at the very edge of the viewport. */
const PARALLAX_X = 14
const PARALLAX_Y = 10

/** One secondary card swaps its content on each tick. */
const ROTATE_EVERY = 4400

interface Place {
    /** Left and top as percentages of the canvas. */
    x: number
    y: number
    /** Width as a percentage of the canvas. Height comes from the content. */
    w: number
    /** Resting tilt, in degrees. */
    rot: number
    /** Root font size, in `cqw`. Everything inside the piece is sized in `em`. */
    fs: number
}

interface PieceBase {
    id: string
    place: Place
    mobile: Place | null
    z: number
    /** Pointer-parallax multiplier. The phone barely moves; doodles move most. */
    depth: number
    /** Entrance start, in ms after mount. */
    delay: number
    /** Rotation delta the entrance starts from, on top of the resting tilt. */
    swing: number
    /** Doodles draw their own strokes on, so their wrapper stays still. */
    entrance: 'spring' | 'slap' | 'none'
    /** Idle drift. Amplitude in px, duration in seconds, spin in degrees. */
    float?: { amp: number, dur: number, spin?: number }
}

type Piece = PieceBase & (
    | { kind: 'phone' }
    | { kind: 'trusted' }
    | { kind: 'card', slot: number }
    | { kind: 'doodle', doodle: DoodleName, tone: DoodleTone }
)

const PIECES: ReadonlyArray<Piece> = [
    /* ---- The phone. Dominant, centred, and the first thing to land ---- */
    {
        id: 'phone',
        kind: 'phone',
        place: { x: 35, y: 7, w: 28.5, rot: 2, fs: 2 },
        mobile: { x: 27, y: 2, w: 46, rot: 2, fs: 3.3 },
        z: 30,
        depth: 0.35,
        delay: 0,
        swing: 4,
        entrance: 'spring',
        float: { amp: -7, dur: 7.2 }
    },

    /* ---- The four listing cards, one per corner ---- */
    {
        id: 'card-username',
        kind: 'card',
        slot: 0,
        place: { x: 4, y: 12, w: 25.5, rot: -3, fs: 2 },
        mobile: { x: 0, y: 4, w: 48, rot: -6, fs: 4.3 },
        z: 20,
        depth: 0.85,
        delay: 120,
        swing: -6,
        entrance: 'spring',
        float: { amp: -5, dur: 5.6 }
    },
    {
        id: 'card-video',
        kind: 'card',
        slot: 1,
        place: { x: 70.5, y: 13.5, w: 25.5, rot: 3.5, fs: 2 },
        mobile: { x: 52, y: 30, w: 48, rot: 5, fs: 4.3 },
        z: 20,
        depth: 0.85,
        delay: 220,
        swing: 7,
        entrance: 'spring',
        float: { amp: -6, dur: 6.4 }
    },
    {
        id: 'card-crypto',
        kind: 'card',
        slot: 2,
        place: { x: 1.5, y: 53, w: 26.5, rot: -2, fs: 2.05 },
        mobile: { x: 2, y: 57, w: 48, rot: -3, fs: 4.3 },
        z: 20,
        depth: 0.95,
        delay: 320,
        swing: -5,
        entrance: 'spring',
        float: { amp: -5, dur: 7.6 }
    },
    {
        /* Dropped on a phone: four cards around a 44%-wide device is a pile,
           not a composition. Its categories live in the other pools too, so
           nothing is unreachable on a small screen. */
        id: 'card-social',
        kind: 'card',
        slot: 3,
        place: { x: 66, y: 56, w: 26.5, rot: 2.5, fs: 2.05 },
        mobile: null,
        z: 20,
        depth: 0.95,
        delay: 420,
        swing: 6,
        entrance: 'spring',
        float: { amp: -4, dur: 6.8 }
    },

    /* ---- The acid sticker. Slapped on, so it overshoots and settles ---- */
    {
        id: 'trusted',
        kind: 'trusted',
        place: { x: 79.5, y: 38, w: 18.5, rot: -7, fs: 2.2 },
        mobile: { x: 53, y: 60, w: 42, rot: -8, fs: 4.6 },
        z: 40,
        depth: 1.1,
        delay: 520,
        swing: -14,
        entrance: 'slap',
        float: { amp: -3, dur: 5.1 }
    },

    /* ---- Marker strokes. Decoration only, drawn on last ---- */
    {
        id: 'doodle-arrow',
        kind: 'doodle',
        doodle: 'arrow',
        tone: 'red',
        place: { x: 4, y: 81.5, w: 7.5, rot: 6, fs: 2 },
        mobile: { x: 2, y: 80, w: 16, rot: 10, fs: 3.6 },
        z: 45,
        depth: 1.3,
        delay: 600,
        swing: 0,
        entrance: 'none',
        float: { amp: -3, dur: 8.4, spin: 2.5 }
    },
    {
        id: 'doodle-loop',
        kind: 'doodle',
        doodle: 'loop',
        tone: 'red',
        place: { x: 14.5, y: 83, w: 7, rot: -6, fs: 2 },
        mobile: null,
        z: 45,
        depth: 1.3,
        delay: 660,
        swing: 0,
        entrance: 'none',
        float: { amp: -2, dur: 9.1, spin: -2 }
    },
    {
        id: 'doodle-hatch',
        kind: 'doodle',
        doodle: 'hatch',
        tone: 'red',
        place: { x: 24.5, y: 82, w: 8, rot: 3, fs: 2 },
        mobile: null,
        z: 45,
        depth: 1.35,
        delay: 720,
        swing: 0,
        entrance: 'none'
    },
    {
        id: 'doodle-spark',
        kind: 'doodle',
        doodle: 'spark',
        tone: 'ink',
        place: { x: 30.8, y: 4.5, w: 4.2, rot: 0, fs: 2 },
        mobile: null,
        z: 15,
        depth: 1.4,
        delay: 780,
        swing: 0,
        entrance: 'none',
        float: { amp: -3, dur: 7.8, spin: 6 }
    },
    {
        id: 'doodle-crown',
        kind: 'doodle',
        doodle: 'crown',
        tone: 'ink',
        place: { x: 64, y: 81, w: 8, rot: -8, fs: 2 },
        mobile: null,
        z: 45,
        depth: 1.3,
        delay: 840,
        swing: 0,
        entrance: 'none'
    },
    {
        id: 'doodle-burst',
        kind: 'doodle',
        doodle: 'burst',
        tone: 'ink',
        place: { x: 74, y: 79.5, w: 7.5, rot: 0, fs: 2 },
        mobile: { x: 82, y: 4, w: 15, rot: 0, fs: 3.6 },
        z: 45,
        depth: 1.35,
        delay: 900,
        swing: 0,
        entrance: 'none',
        float: { amp: -2, dur: 8.8, spin: 5 }
    },
    {
        id: 'doodle-star',
        kind: 'doodle',
        doodle: 'star',
        tone: 'ink',
        place: { x: 83, y: 84.5, w: 5, rot: 12, fs: 2 },
        mobile: null,
        z: 45,
        depth: 1.4,
        delay: 960,
        swing: 0,
        entrance: 'none',
        float: { amp: -2, dur: 7.4, spin: -4 }
    },
    {
        id: 'doodle-zigzag',
        kind: 'doodle',
        doodle: 'zigzag',
        tone: 'ink',
        place: { x: 89.5, y: 78.5, w: 6.5, rot: -4, fs: 2 },
        mobile: null,
        z: 45,
        depth: 1.35,
        delay: 1020,
        swing: 0,
        entrance: 'none'
    }
]

/**
 * Placement is written as custom properties rather than inline `left`/`top`,
 * because the values differ per breakpoint and only CSS can pick between them.
 * They are strings on purpose: each is consumed inside `calc()` with an
 * explicit unit, so a bare number is what every one of these call sites wants.
 */
const pieceVars = (piece: Piece): CSSProperties => {
    const { place } = piece
    const small = piece.mobile ?? place

    return {
        '--x': String(place.x),
        '--y': String(place.y),
        '--w': String(place.w),
        '--rot': String(place.rot),
        '--fs': String(place.fs),
        '--m-x': String(small.x),
        '--m-y': String(small.y),
        '--m-w': String(small.w),
        '--m-rot': String(small.rot),
        '--m-fs': String(small.fs),
        zIndex: piece.z
    } as CSSProperties
}

interface HeroPieceProps {
    piece: Piece
    pointerX: MotionValue<number>
    pointerY: MotionValue<number>
    children: ReactNode
}

const HeroPiece: FC<HeroPieceProps> = ({ piece, pointerX, pointerY, children }): ReactNode => {
    const reduced = useReducedMotion()
    const x = useTransform(pointerX, (value) => value * piece.depth)
    const y = useTransform(pointerY, (value) => value * piece.depth)

    const delay = piece.delay / 1000
    const still = piece.entrance === 'none'

    /* A slapped sticker overshoots; everything else lands firmly. Opacity is
       given its own tween because a spring can overshoot past 1 and flicker. */
    const settle = piece.entrance === 'slap'
        ? { type: 'spring' as const, stiffness: 380, damping: 13, mass: 0.8, delay }
        : { type: 'spring' as const, stiffness: 240, damping: 20, mass: 0.9, delay }

    return (
        <div
            style={pieceVars(piece)}
            className={cn('mh-piece', piece.mobile === null && 'max-sm:hidden')}
        >
            <motion.div style={{ x, y }}>
                <motion.div
                    initial={still ? false : { opacity: 0, scale: 0.85, y: 24, rotate: piece.swing }}
                    animate={{ opacity: 1, scale: 1, y: 0, rotate: 0 }}
                    transition={
                        reduced
                            ? { duration: 0 }
                            : { default: settle, opacity: { duration: 0.34, delay, ease: 'easeOut' } }
                    }
                >
                    <motion.div
                        animate={
                            reduced || !piece.float
                                ? { y: 0, rotate: 0 }
                                : {
                                    y: [0, piece.float.amp, 0],
                                    rotate: piece.float.spin ? [0, piece.float.spin, 0] : 0
                                }
                        }
                        transition={
                            reduced || !piece.float
                                ? { duration: 0 }
                                : {
                                    duration: piece.float.dur,
                                    repeat: Infinity,
                                    ease: 'easeInOut',
                                    delay: delay + 0.6
                                }
                        }
                    >
                        {children}
                    </motion.div>
                </motion.div>
            </motion.div>
        </div>
    )
}

interface MarketHeroVisualProps {
    className?: string
}

export const MarketHeroVisual: FC<MarketHeroVisualProps> = ({ className }): ReactNode => {
    const canvas = useRef<HTMLDivElement>(null)
    const reduced = useReducedMotion()

    /** One index per card pool. A tick advances exactly one of them. */
    const [picks, setPicks] = useState<ReadonlyArray<number>>(() => HERO_LISTING_POOLS.map(() => 0))
    const turn = useRef(0)

    const pointerX = useMotionValue(0)
    const pointerY = useMotionValue(0)
    const smoothX = useSpring(pointerX, { stiffness: 110, damping: 20, mass: 0.55 })
    const smoothY = useSpring(pointerY, { stiffness: 110, damping: 20, mass: 0.55 })

    /**
     * Pointer parallax. Desktop pointers only, and never for a visitor who
     * asked for reduced motion. The handler writes two motion values and
     * nothing else — no state, no re-render, no per-frame layout read — and
     * each piece scales them by its own depth.
     */
    useEffect(() => {
        if (!canvas.current) return

        const fine = window.matchMedia('(hover: hover) and (pointer: fine)')
        const calm = window.matchMedia('(prefers-reduced-motion: reduce)')

        if (!fine.matches || calm.matches) return

        let frame = 0

        const onMove = (event: PointerEvent): void => {
            if (frame) return

            frame = window.requestAnimationFrame(() => {
                frame = 0

                pointerX.set((event.clientX / window.innerWidth - 0.5) * 2 * PARALLAX_X)
                pointerY.set((event.clientY / window.innerHeight - 0.5) * 2 * PARALLAX_Y)
            })
        }

        window.addEventListener('pointermove', onMove, { passive: true })

        return () => {
            window.removeEventListener('pointermove', onMove)

            if (frame) window.cancelAnimationFrame(frame)
        }
    }, [pointerX, pointerY])

    /**
     * The live marketplace. One card at a time advances through its own pool,
     * round-robin, so the composition keeps changing without ever redrawing
     * itself. It is off entirely under reduced motion — content that rewrites
     * itself on a timer is exactly what that preference asks us not to do —
     * and it pauses on a hidden tab rather than burning a phone's battery in
     * a background window.
     */
    useEffect(() => {
        if (reduced) return

        const id = window.setInterval(() => {
            if (document.visibilityState !== 'visible') return

            const slot = turn.current % HERO_LISTING_POOLS.length

            turn.current += 1

            setPicks((prev) => prev.map((value, index) => (
                index === slot ? (value + 1) % HERO_LISTING_POOLS[index].length : value
            )))
        }, ROTATE_EVERY)

        return () => window.clearInterval(id)
    }, [reduced])

    const content = (piece: Piece): ReactNode => {
        switch (piece.kind) {
            case 'phone':
                return <MarketPhone />

            case 'trusted':
                return <TrustedBadge />

            case 'card': {
                const pool = HERO_LISTING_POOLS[piece.slot]

                return (
                    <FloatingListingCard
                        listing={pool[picks[piece.slot] % pool.length]}
                        restRotation={piece.place.rot}
                    />
                )
            }

            case 'doodle':
                return <HeroDoodle name={piece.doodle} tone={piece.tone} delay={piece.delay} />
        }
    }

    return (
        <div
            ref={canvas}
            role='img'
            aria-label={SUMMARY}
            className={cn('mh-canvas', className)}
        >
            {/*
                The entrance's START state is what gets rendered into the
                server HTML, so with scripting off the composition would sit
                at opacity 0 forever and the marker strokes would never be
                drawn. This lands every piece in its settled state instead.
                `dangerouslySetInnerHTML` rather than children, because the
                browser parses noscript content as TEXT while scripting is on
                and React would otherwise report a hydration mismatch.
            */}
            <noscript
                dangerouslySetInnerHTML={{
                    __html: '<style>.mh-piece *{opacity:1!important;transform:none!important}.mh-piece path{stroke-dasharray:none!important;stroke-dashoffset:0!important}</style>'
                }}
            />

            {PIECES.map((piece) => (
                <HeroPiece key={piece.id} piece={piece} pointerX={smoothX} pointerY={smoothY}>
                    {content(piece)}
                </HeroPiece>
            ))}
        </div>
    )
}
